import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import theme from '../theme';
import { fetchAccounts, fetchAssets, fetchFavoritesDashboard, fetchUserProfile, saveHitFavorites } from '../services/api';
import NewHomePage from './NewHomePage';
import NotificationFeedInline from '../components/Notifications/NotificationFeedInline';
import MultiSelect, { plain } from '../components/common/MultiSelect';
import useInstrumentFolders from '../components/Notifications/useInstrumentFolders';
import { EVENT as HIT_FAVORITES_EVENT } from '../components/Notifications/useHitFavorites';
import {
  KINDS, SCANNER, accountToken, assetSources, filterCards, folderToken, isScannerCard, migrateFilters, rowLabel, toCards,
  unstarPlan,
} from './favoritesDashboard';

// ponytail: polled, the rows are a few Redis reads; a push channel if it ever lags.
const REFRESH_MS = 15000;
const FILTERS_KEY = 'favorites-dashboard-filters';
const EMPTY_FILTERS = { sectypes: [], kinds: [], placement: [] };

const readFilters = () => {
  try {
    return { ...EMPTY_FILTERS, ...migrateFilters(JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}')) };
  } catch (e) {
    return EMPTY_FILTERS;
  }
};

const assetKey = (a) => `${a.broker}|${a.account}|${a.symbol}|${a.sectype || ''}`;

// `expanded` = the asset captured on click: polls rebuild card.asset, and a new
// object would reconnect the panel every refresh.
// `onUnstar(card, timeframes)` takes stars off; it works on a card that cannot open too,
// so a star of a removed instrument can still be cleaned up.
function FavoriteCard({ card, expanded, onOpen, onUnstar, busy }) {
  const disabled = !card.asset;
  const open = () => { if (!disabled) onOpen(card); };
  // The star sits on a timeframe's first row; its other pairs share it.
  const firstOfTf = new Set();
  const unstar = (e, timeframes) => { e.stopPropagation(); onUnstar(card, timeframes); };
  return (
    // Expanded: as wide as the scanner panel (max-w-md + padding) in its own place, not the whole row.
    <div className={`${theme.cardBg} ${theme.cardBorder} max-w-full rounded-2xl ${expanded ? 'w-[30rem]' : 'w-44'} ${busy ? 'opacity-50' : ''}`}>
      {/* A div, not a button: the star buttons live inside it. */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        data-testid={`card-${card.symbol}`}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        title={disabled ? 'Instrument not found on its account' : isScannerCard(card) ? 'Open scanner panel' : 'Open Solo panel'}
        className={`w-full rounded-2xl p-3 text-left ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-olive-50'}`}
      >
        <div className="flex items-center justify-between gap-2 border-b border-olive-100 pb-1.5">
          <span className={`font-semibold ${theme.textPrimary} ${disabled ? 'opacity-50' : ''}`}>{card.symbol}</span>
          <span className="flex items-center gap-1">
            <span className={`text-[10px] uppercase ${theme.textMuted}`}>
              {[card.asset?.sectype, isScannerCard(card) ? 'scanner' : null].filter(Boolean).join(' · ')}
            </span>
            <button
              type="button"
              disabled={busy}
              onClick={(e) => unstar(e, card.starred)}
              title="Remove from Favorites (all its stars)"
              aria-label={`Remove ${card.symbol} from Favorites`}
              className="rounded px-1 text-xs text-olive-400 hover:bg-rose-50 hover:text-rose-500"
            >
              ✕
            </button>
          </span>
        </div>
        <ul className="mt-1.5 space-y-0.5 font-mono text-xs">
          {card.rows.map((r) => {
            const first = !firstOfTf.has(r.tf);
            firstOfTf.add(r.tf);
            return (
              <li key={`${r.tf}|${r.senior}|${r.kind}`} className={`flex items-center justify-between gap-1 ${r.kind ? theme.textPrimary : theme.textMuted}`}>
                <span>{rowLabel(r)}</span>
                {first && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={(e) => unstar(e, [r.tf])}
                    title={`Unstar ${r.tf}`}
                    aria-label={`Unstar ${r.tf}`}
                    className="shrink-0 rounded px-0.5 text-base leading-none text-amber-500 hover:text-olive-300"
                  >
                    ★
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {expanded && (
        <div className="border-t border-olive-100 p-3">
          <NewHomePage focus={expanded} />
        </div>
      )}
    </div>
  );
}

export default function FavoritesPage() {
  const [favorites, setFavorites] = useState(null);
  const [failed, setFailed] = useState(false);
  const [assets, setAssets] = useState([]);
  const [filters, setFilters] = useState(readFilters);
  // { mode: 'solo' | 'scanner', asset }: the left panel shows this instrument instead of the cards.
  const [focus, setFocus] = useState(null);
  // { key, mode: 'scanner', asset }: the scanner card opened in place.
  const [expanded, setExpanded] = useState(null);
  const loadedSources = useRef(null);
  const [feedAll, setFeedAll] = useState(false);
  const [rightTab, setRightTab] = useState('feed');
  // The one filter dropdown open, if any.
  const [openFilter, setOpenFilter] = useState(null);
  // broker|symbol whose stars are being saved; its buttons wait.
  const [busyKey, setBusyKey] = useState(null);
  const [unstarFailed, setUnstarFailed] = useState(null);
  const folderOf = useInstrumentFolders();

  const [accounts, setAccounts] = useState([]);
  const [username, setUsername] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const profile = await fetchUserProfile();
        if (alive) setUsername(profile?.name || null);
      } catch (e) { /* the label falls back to "…" */ }
    })();
    return () => { alive = false; };
  }, []);
  const accountsRef = useRef(null);
  const assetsRef = useRef([]);
  const loadSeq = useRef(0);
  const askedMissing = useRef(new Set());
  const load = useCallback(async () => {
    // A slower older read (a poll racing a star) must not land over a newer one.
    const seq = ++loadSeq.current;
    const favs = await fetchFavoritesDashboard();
    if (seq !== loadSeq.current) return;
    if (favs === null) {
      setFailed(true);
      return;
    }
    setFailed(false);
    setFavorites(favs);
    // Asset rows change at operator speed: re-read when the accounts change, or
    // when a star names an instrument the loaded lists lack (once per such set,
    // so an instrument really gone is not re-asked every poll).
    // Every linked account too, not only the starred ones: "Trading on" asks
    // where an instrument trades, wherever it was starred.
    if (!accountsRef.current) {
      accountsRef.current = (await fetchAccounts()) || [];
      if (seq !== loadSeq.current) return;
      setAccounts(accountsRef.current);
    }
    const sources = assetSources([...favs, ...accountsRef.current]);
    const sourcesKey = JSON.stringify(sources);
    const missing = toCards(favs, assetsRef.current).filter((c) => !c.asset).map((c) => c.key);
    const unasked = missing.filter((k) => !askedMissing.current.has(k));
    if (loadedSources.current === sourcesKey && unasked.length === 0) return;
    loadedSources.current = sourcesKey;
    missing.forEach((k) => askedMissing.current.add(k));
    const lists = await Promise.all(sources.map((s) => fetchAssets(s.account, s.broker, true)));
    if (seq !== loadSeq.current) return;
    assetsRef.current = lists.flat().filter(Boolean);
    setAssets(assetsRef.current);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    // A star set in an opened panel shows up at once, not on the next poll.
    window.addEventListener(HIT_FAVORITES_EVENT, load);
    return () => { clearInterval(t); window.removeEventListener(HIT_FAVORITES_EVENT, load); };
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem(FILTERS_KEY, JSON.stringify(filters)); } catch (e) { /* private mode */ }
  }, [filters]);

  const cards = useMemo(() => toCards(favorites || [], assets), [favorites, assets]);
  const shown = filterCards(cards, filters, folderOf);
  const sectypeOptions = [...new Set(cards.map((c) => c.asset?.sectype).filter(Boolean))].sort();
  // Where the instrument sits now: in the scanner (a folder) or trading on an account.
  const placementGroups = [
    {
      title: 'Scanner',
      options: [
        { value: SCANNER, label: 'Any folder' },
        ...[...new Set(Object.values(folderOf))].sort().map((name) => ({ value: folderToken(name), label: name, indent: true })),
      ],
    },
    {
      title: 'Trading on',
      options: accounts.filter((a) => a.account).map((a) => ({
        value: accountToken(a.broker, a.account),
        // OKX is known by the platform username, not its stored account value.
        label: `${a.broker.toUpperCase()} · ${a.broker === 'okx' ? (username || '…') : a.account}`,
      })),
    },
  ];

  // Saves per account; every success is announced so open panels' stars follow,
  // and the page reloads either way, so a failed account keeps its star on screen.
  const unstar = async (card, timeframes) => {
    setBusyKey(card.key);
    setUnstarFailed(null);
    const results = await Promise.all(unstarPlan(favorites || [], card.broker, card.symbol, timeframes).map(async (p) => {
      const saved = await saveHitFavorites(p.account, p.broker, p.symbol, p.timeframes);
      if (saved) {
        window.dispatchEvent(new CustomEvent(HIT_FAVORITES_EVENT, {
          detail: { account: p.account, broker: p.broker, symbol: p.symbol, favs: saved },
        }));
      }
      return saved;
    }));
    if (results.some((r) => !r)) setUnstarFailed(card.symbol);
    if (expanded?.key === card.key && timeframes.length === card.starred.length) setExpanded(null);
    setBusyKey(null);
    await load();
  };

  const openCard = (card) => {
    if (isScannerCard(card)) {
      setExpanded((e) => (e?.key === card.key ? null : { key: card.key, mode: 'scanner', asset: card.asset }));
      return;
    }
    setFocus({ mode: 'solo', asset: card.asset });
  };

  // A feed item opens its instrument's panel in place of the cards, favourite or not.
  const openFromFeed = async (n) => {
    if (!n?.symbol || !n.broker) return;
    const match = (a) => a.broker === n.broker && a.symbol === n.symbol && (n.broker === 'okx' || a.account === n.account);
    let asset = assets.find(match);
    if (!asset && n.account) asset = ((await fetchAssets(n.account, n.broker, true)) || []).find(match);
    if (!asset) return;
    setFocus({ mode: asset.trading === false ? 'scanner' : 'solo', asset });
  };

  const focusPair = focus ? `${focus.asset.broker}|${focus.asset.symbol}` : null;
  const feedContext = focusPair && !feedAll ? { pairs: [focusPair] } : {};
  // Same identity across renders while the stars hold, so the feed does not reload on every poll.
  const starsKey = JSON.stringify((favorites || []).map((f) => [f.account, f.broker, f.symbol, f.timeframes]));
  const favoriteStars = useMemo(() => (favorites || []).map(({ account, broker, symbol, timeframes }) => ({
    account, broker, symbol, timeframes,
  })), [starsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-bold">Dashboard Favorites</h1>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <section className={`${theme.cardBg} ${theme.cardBorder} min-w-0 rounded-2xl p-4 xl:col-span-2`}>
          {focus ? (
            <>
              <div className="mb-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => { setFocus(null); load(); }}
                  className="rounded-lg bg-white px-3 py-2 text-xs font-bold text-gray-500 shadow hover:text-olive-600"
                >
                  ← FAVORITES
                </button>
                <span className="text-sm font-bold text-gray-700">
                  {focus.asset.symbol}{focus.asset.broker === 'okx' ? '' : ` @ ${focus.asset.account}`}
                </span>
              </div>
              <NewHomePage key={assetKey(focus.asset)} focus={focus} />
            </>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className={`text-lg font-semibold ${theme.textPrimary}`}>Favorites</h2>
                <div className="flex flex-wrap gap-2">
                  {[
                    ['Sectype', 'sectypes', plain(sectypeOptions)],
                    ['Indicator', 'kinds', plain(KINDS)],
                    ['Placement', 'placement', placementGroups],
                  ].map(([label, field, groups]) => (
                    <MultiSelect
                      key={field}
                      label={label}
                      groups={groups}
                      value={filters[field]}
                      onChange={(v) => setFilters((f) => ({ ...f, [field]: v }))}
                      open={openFilter === field}
                      onOpenChange={(o) => setOpenFilter(o ? field : null)}
                    />
                  ))}
                </div>
              </div>
              {failed && <p className="mb-2 text-sm font-bold text-red-600">Could not load favorites</p>}
              {unstarFailed && <p className="mb-2 text-sm font-bold text-red-600">Could not remove a star of {unstarFailed}</p>}
              {favorites === null && !failed ? (
                <p className={`py-8 text-center text-sm ${theme.textMuted}`}>Loading…</p>
              ) : cards.length === 0 && !failed ? (
                <p className={`py-8 text-center text-sm ${theme.textMuted}`}>
                  No favorites yet — star timeframes in an instrument&apos;s Hits tab.
                </p>
              ) : shown.length === 0 && !failed ? (
                <p className={`py-8 text-center text-sm ${theme.textMuted}`}>Nothing matches these filters.</p>
              ) : (
                <div className="flex flex-wrap items-start gap-3">
                  {shown.map((card) => (
                    <FavoriteCard
                      key={card.key}
                      card={card}
                      expanded={expanded?.key === card.key ? expanded : null}
                      onOpen={openCard}
                      onUnstar={unstar}
                      busy={busyKey === card.key}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <aside className={`${theme.cardBg} ${theme.cardBorder} min-w-0 overflow-hidden rounded-2xl`}>
          <div className="flex items-center gap-1 border-b border-olive-100 px-3 pt-2">
            {[{ key: 'feed', label: 'Live Feed' }, { key: 'ai', label: 'AI Assistant' }].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setRightTab(t.key)}
                className={`rounded-t-lg px-3 py-1.5 text-sm font-medium ${rightTab === t.key ? `${theme.pillActive}` : theme.pillInactive}`}
              >
                {t.label}
              </button>
            ))}
            {rightTab === 'feed' && focusPair && (
              <button
                type="button"
                onClick={() => setFeedAll((v) => !v)}
                title="Focus shows its instrument's feed; switch to see everything"
                className={`ml-auto rounded-lg px-2 py-1 text-[11px] font-medium ${theme.pillInactive}`}
              >
                {feedAll ? `Only ${focus.asset.symbol}` : 'Show all'}
              </button>
            )}
          </div>
          {rightTab === 'feed' ? (
            <NotificationFeedInline
              key={JSON.stringify(feedContext)}
              framed={false}
              page
              context={feedContext}
              favoriteStars={favoriteStars}
              title={focusPair && !feedAll ? `Live feed · ${focus.asset.symbol}` : 'Live feed'}
              onSelectItem={openFromFeed}
            />
          ) : (
            <p className={`px-4 py-16 text-center text-sm ${theme.textMuted}`}>Coming soon</p>
          )}
        </aside>
      </div>
    </div>
  );
}
